import {combineLatest} from 'rxjs';
import {map} from 'rxjs/operators';
import {
  findTakeProfitOrders,
  findStopLossOrders,
  findTrailingStopOrders,
  isTakeProfitOrder,
  isStopLossOrder,
} from './order-filters';
import {positions$} from '@/positions/positions';
import {
  contractConditionalOrders$,
  contractConditionalOrdersAll$,
} from '@/orders-conditional-contract/order-conditional-contract';
import type {TpsltsRecord, ContractConditionalOrder, Position} from '@/types';
import {airdropPositions$} from '@/positions/all/positions-all';

type SymbolOrderMap = Map<string, ContractConditionalOrder[]>;

const symbolOrderMaps$ = contractConditionalOrders$.pipe(
  map((orders) =>
    orders.reduce((m, cur) => {
      const {symbol} = cur;
      m.has(symbol) ? m.get(symbol).push(cur) : m.set(symbol, [cur]);
      return m;
    }, new Map() as SymbolOrderMap)
  )
);

export const tpsltsOrders$ = combineLatest([positions$, symbolOrderMaps$]).pipe(
  map(([positions, symbolOrderMaps]) =>
    positions
      .filter((p) => p.size > 0)
      .reduce((acc, p) => {
        const [key, val] = createTpsltsRecord(p, symbolOrderMaps.get(p.symbol) || []);
        return acc.set(key, val);
      }, new Map<string, TpsltsRecord>())
  )
);

export const airdropPosTpsltsOrders$ = combineLatest([airdropPositions$, contractConditionalOrdersAll$]).pipe(
  map(([positions, orders]) => {
    const orderMap = orders.reduce((p, c) => {
      if (positions.find((o) => o.userID === c.userID)) {
        const key = c.userID + '_' + c.curPosTerm;
        if (p.has(key)) {
          p.set(key, [...p.get(key), c]);
        } else {
          p.set(key, [c]);
        }
      }
      return p;
    }, new Map());
    orderMap.forEach((val, key) => {
      const tpOrders = val.filter((o) => isTakeProfitOrder(o));
      const slOrders = val.filter((o) => isStopLossOrder(o));
      orderMap.set(key, {
        tp: {
          Entire: tpOrders,
          Partial: [],
          All: tpOrders,
        },
        sl: {
          Entire: slOrders,
          Partial: [],
          All: slOrders,
        },
        ts: {
          All: [],
        },
      });
    });
    return orderMap;
  })
);

function createTpsltsRecord(position: Position, orders: ContractConditionalOrder[]): [string, TpsltsRecord] {
  return [
    position.symbol + position.posSide,
    {
      tp: {
        Entire: findTakeProfitOrders(orders, position, 'Entire'),
        Partial: findTakeProfitOrders(orders, position, 'Partial'),
        All: findTakeProfitOrders(orders, position, 'All'),
      },
      sl: {
        Entire: findStopLossOrders(orders, position, 'Entire'),
        Partial: findStopLossOrders(orders, position, 'Partial'),
        All: findStopLossOrders(orders, position, 'All'),
      },
      ts: {
        All: findTrailingStopOrders(orders, position),
      },
    },
  ];
}
